import React from 'react';
import moment from 'moment';
import bemBlock from '@gik/core/utils/bemBlock';
import { SWRLongCachingOptions } from '@gik/core/constants';
import PageRowItem from './PageRowItem';
import { InkindPageAPIModel } from '@gik/core/models/gik/InkindPage';
import { FrameBone, ParagraphBone } from '@gik/ui/SkeletonLoader';
import { useUserStore } from '@gik/core/store/UserStore';
import { useUserPages } from '@gik/api/inkinds/userPages';
import withSuspense from '@gik/core/components/suspended/withSuspense';
import { isInkindPageOwner, isInkindPageOrganizer } from '@gik/inkind-page/utils/inkindPagePermissions';

export interface IPageRowsProps extends React.HTMLAttributes<HTMLDivElement> {
  pages?: InkindPageAPIModel[];
  skeleton?: boolean;
  skeletonItems?: number;
}

const skelBem = bemBlock(`page-rows-skeleton`);
const Skeleton = ({ skeletonItems = 4 }: Partial<IPageRowsProps>): React.ReactElement => (
  <div className={`${skelBem()}`}>
    {Array.from(Array(skeletonItems).keys()).map(index => {
      return (
        <div key={index} className={skelBem('item')}>
          <FrameBone className={skelBem('image')} />
          <main>
            <ParagraphBone words={3} className={skelBem('text')} />
          </main>
        </div>
      );
    })}
  </div>
);

function PageRows({ className, pages }: React.PropsWithChildren<IPageRowsProps>): React.ReactElement {
  const bem = bemBlock('page-rows');

  const { id: userId } = useUserStore();
  const { data: userPages, error: userPagesError } = useUserPages(userId, {
    ...SWRLongCachingOptions,
    initialData: pages,
  });

  const sortInkindPages = (pages: InkindPageAPIModel[], userId: string): InkindPageAPIModel[] => {
    if (!pages || !userId) return [];

    const ownedPages = pages.filter(page => isInkindPageOwner(page, userId));
    const organizedPages = pages.filter(page => isInkindPageOrganizer(page, userId));
    const followedPages = pages.filter(
      page => !isInkindPageOwner(page, userId) && !isInkindPageOrganizer(page, userId)
    );

    sortInkindPagesByDate(ownedPages);
    sortInkindPagesByDate(organizedPages);
    sortInkindPagesByDate(followedPages);

    const sortedPages = [...ownedPages, ...organizedPages, ...followedPages];

    return sortedPages;
  };

  const sortInkindPagesByDate = (pages: InkindPageAPIModel[]): void => {
    pages &&
      pages.length > 0 &&
      pages.sort((a, b) => (moment.utc(a.createdUtc).isAfter(moment.utc(b.createdUtc)) ? -1 : 1));
  };

  const isLoading = !userPages;
  const isError = isLoading && userPagesError;

  if (isLoading) return <Skeleton />;
  if (isError) return <Skeleton />;

  const sortedUserPages = sortInkindPages(userPages, userId);

  return (
    <ul className={bem(null, null, className)}>
      {sortedUserPages?.length > 0 &&
        sortedUserPages.map(page => {
          return <PageRowItem key={page.id} page={page} className={bem(null, null, className)} />;
        })}
    </ul>
  );
}

export default withSuspense(PageRows, Skeleton);
